import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  DropdownItem,
  formatCodeName,
  getGroupDescriptionRef,
  OutputDescriptionProcessingStatesType,
  OutputDescriptionProcessingStateType,
  Table,
  TextNormal,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  Dropdown,
  Flex,
  Radio,
} from 'antd';
import React, { FC, useCallback, useMemo, memo } from 'react';
import styled from 'styled-components';
import { UiLink } from '../../../paths/UiLink';
import translations from './ActiveOutputsTable.translations.json';
import { VersionCell } from '../VersionCell';
import { ErrorLevel } from '../ErrorLevel';
import {
  ActiveProcessingCell,
  getActiveProcessingTypeLabel,
} from '../ActiveProcessingCell';
import * as Icon from 'react-feather';
import { path } from '../../../paths';

const { addTranslations, t } = i18nNsFactory('active-outputs-table');
addTranslations(translations);

interface ActiveOutputsBaseProps {
  mode: 'read' | 'edit';
  data: OutputDescriptionProcessingStatesType;
  hasGroupAccess: boolean;
}

interface ActiveOutputsNoSelectProps extends ActiveOutputsBaseProps {
  selectionMode: 'none';
  isSelectable?: never;
  selectedOutputId?: never;
  selectedOutputIds?: never;
  onSelectedOutputIdChange?: never;
  onSelectedOutputIdsChange?: never;
}

interface ActiveOutputsSingleSelectProps extends ActiveOutputsBaseProps {
  selectionMode: 'single';
  isSelectable?: (output: OutputDescriptionProcessingStateType) => boolean;
  selectedOutputId?: number;
  onSelectedOutputIdChange: (id: number | undefined) => void;
}

interface ActiveOutputsMultiSelectProps extends ActiveOutputsBaseProps {
  selectionMode: 'multiple';
  isSelectable?: (output: OutputDescriptionProcessingStateType) => boolean;
  selectedOutputIds: number[];
  onSelectedOutputIdsChange: (ids: number[]) => void;
}

type ActiveOutputsTableProps =
  | ActiveOutputsNoSelectProps
  | ActiveOutputsSingleSelectProps
  | ActiveOutputsMultiSelectProps;

const columnHelper = createColumnHelper<OutputDescriptionProcessingStateType>();

export const ActiveOutputsTable: FC<ActiveOutputsTableProps> = (props) => {
  const { mode, data, isSelectable = () => true, hasGroupAccess } = props;

  const selectableOutputIds = useMemo(
    () =>
      data?.outputs
        .filter((output) => isSelectable(output))
        .map((output) => output.id) || [],
    [data?.outputs, isSelectable],
  );

  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'FREQUENCY',
    'OUTPUT_TYPE',
    'PROCESSING_TYPE',
    'PROCESSING_METHOD',
    'DATA_PROCESSING_SCOPE',
    'PROC_VAL_ERROR_LEVEL',
  ]);

  const classifierMaps = useMemo(
    () => ({
      processingTypes: systemClassifierItemsById['PROCESSING_TYPE'],
      processingMethods: systemClassifierItemsById['PROCESSING_METHOD'],
      processingScopes: systemClassifierItemsById['DATA_PROCESSING_SCOPE'],
      validationErrorLevels: systemClassifierItemsById['PROC_VAL_ERROR_LEVEL'],
    }),
    [systemClassifierItemsById],
  );

  const columns = useMemo(
    () => [
      ...(mode === 'edit'
        ? [
            columnHelper.display({
              id: 'selected',
              header:
                props.selectionMode === 'multiple'
                  ? () => (
                      <SelectionHeader
                        selectedOutputIds={props.selectedOutputIds}
                        allSelectableOutputIds={selectableOutputIds}
                        onSelectedOutputIdsChange={
                          props.onSelectedOutputIdsChange
                        }
                      />
                    )
                  : undefined,
              cell: (rowProps) =>
                props.selectionMode !== 'none' &&
                isSelectable(rowProps.row.original) && (
                  <SelectionCell {...rowProps} {...props} />
                ),
              meta: { width: 32, className: 'sticky-left' },
              enableColumnFilter: false,
              enableSorting: false,
            }),
          ]
        : []),
      columnHelper.accessor((rowData) => formatCodeName(rowData), {
        id: 'name',
        header: t('column.name'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { minWidth: '16ch' },
      }),
      columnHelper.accessor(
        (rowData) =>
          systemClassifierItemsById['OUTPUT_TYPE'].get(rowData.outputTypeId)
            ?.name,
        {
          id: 'outputType',
          header: t('column.outputType'),
          enableColumnFilter: false,
          enableSorting: false,
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          systemClassifierItemsById['FREQUENCY'].get(rowData.frequencyId)?.name,
        {
          id: 'frequency',
          header: t('column.frequency'),
          enableColumnFilter: false,
          enableSorting: false,
        },
      ),
      columnHelper.accessor('activeDataObjectVersionId', {
        header: t('column.version'),
        cell: (props) => (
          <VersionCell
            version={props.row.original.activeDataObjectVersionNumber}
            versionStatus={props.row.original.activeDataObjectVersionStatus}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.accessor('activeDataObjectVersionValidationErrorLevelId', {
        header: t('column.versionValidationErrors'),
        cell: (props) => (
          <ErrorLevel
            outputDataObjectId={props.row.original.id}
            missingSourceData={
              props.row.original.activeDataObjectVersionIsMissingSourceData ||
              false
            }
            calculationErrors={
              props.row.original.activeDataObjectCalculationErrors || false
            }
            versionValidationErrorLevelId={
              props.row.original.activeDataObjectVersionValidationErrorLevelId
            }
            type="processingGroup"
            typeId={props.row.original.processingGroupId ?? 0}
            validationErrorLevels={
              (classifierMaps as any).validationErrorLevels
            }
            hasAccess={hasGroupAccess}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '24ch',
        },
      }),
      columnHelper.accessor(
        (processingState) =>
          getActiveProcessingTypeLabel({
            processingStateCode: processingState.processingStateCode,
            processing: processingState,
            ...(classifierMaps as any),
          }),
        {
          id: 'processingStateCode',
          header: t('column.processingState'),
          cell: ActiveProcessingCell as any,
          enableColumnFilter: false,
          enableSorting: false,
          meta: {
            width: '16ch',
          },
        },
      ),
      columnHelper.display({
        id: 'operations',
        header: t('column.operations'),
        cell: (props) => (
          <OutputDataActionCell
            {...props}
            valueDate={data.valueDate}
            groupId={data.groupId}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 74,
          className: 'sticky-right',
        },
      }),
    ],
    [
      mode,
      props,
      selectableOutputIds,
      isSelectable,
      systemClassifierItemsById,
      classifierMaps,
      hasGroupAccess,
      data.valueDate,
      data.groupId,
    ],
  );

  return (
    <TableWrapper isRead={mode === 'read'}>
      <Table
        name="output-data-active-info-table"
        columns={columns}
        data={data.outputs}
        defaultSort={[{ id: 'name', desc: false }]}
        rowHtmlAttributes={(row) => ({
          className: row.outputDataObjectId ? 'intermediate' : '',
        })}
        persistColumnStyle={false}
      />
    </TableWrapper>
  );
};

type SelectionHeaderProps = {
  selectedOutputIds: number[];
  allSelectableOutputIds: number[];
  onSelectedOutputIdsChange: (ids: number[]) => void;
};

const SelectionHeader: React.FC<SelectionHeaderProps> = ({
  selectedOutputIds,
  allSelectableOutputIds,
  onSelectedOutputIdsChange,
}) => {
  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) => {
      onSelectedOutputIdsChange(e.target.checked ? allSelectableOutputIds : []);
      return;
    },
    [allSelectableOutputIds, onSelectedOutputIdsChange],
  );

  return (
    <Flex justify="center">
      <Checkbox
        id="select-all"
        disabled={allSelectableOutputIds.length === 0}
        checked={
          selectedOutputIds.length > 0 &&
          selectedOutputIds.length === allSelectableOutputIds.length
        }
        indeterminate={
          selectedOutputIds.length > 0 &&
          selectedOutputIds.length < allSelectableOutputIds.length
        }
        onChange={onCheckAllChange}
      />
    </Flex>
  );
};

const SelectionCell: FC<
  CellContext<OutputDescriptionProcessingStateType, any> & {
    selectionMode: 'single' | 'multiple';
    selectedOutputId?: number;
    onSelectedOutputIdChange?: (id: number | undefined) => void;
    selectedOutputIds?: number[];
    onSelectedOutputIdsChange?: (ids: number[]) => void;
  }
> = memo((props) => {
  const { id: outputId } = props.row.original;
  const {
    selectionMode,
    selectedOutputId,
    onSelectedOutputIdChange,
    selectedOutputIds,
    onSelectedOutputIdsChange,
  } = props;

  const onCheckChange = useCallback(
    (checked: boolean, id: number) => {
      if (selectionMode === 'single') {
        return checked
          ? onSelectedOutputIdChange?.(id)
          : onSelectedOutputIdChange?.(undefined);
      } else if (selectionMode === 'multiple') {
        const currentSelected = new Set(props.selectedOutputIds);
        checked ? currentSelected.add(id) : currentSelected.delete(id);
        onSelectedOutputIdsChange?.(Array.from(currentSelected));
      }
    },
    [
      onSelectedOutputIdChange,
      onSelectedOutputIdsChange,
      props.selectedOutputIds,
      selectionMode,
    ],
  );

  return (
    <Flex justify="center">
      {selectionMode === 'multiple' ? (
        <Checkbox
          id={`selected-${outputId}`}
          checked={selectedOutputIds?.includes(outputId)}
          onChange={(e) => onCheckChange(e.target.checked, outputId)}
        />
      ) : (
        <Radio
          id={`selected-${outputId}`}
          checked={selectedOutputId === outputId}
          onChange={() => onCheckChange(true, outputId)}
        />
      )}
    </Flex>
  );
});

const OutputDataActionCell: FC<
  CellContext<OutputDescriptionProcessingStateType, any> & {
    groupId: number;
    valueDate: string;
  }
> = memo((props) => {
  const { groupId, valueDate } = props;
  const {
    id,
    activeDataObjectDescriptionVersionId,
    activeDataObjectVersionId,
    outputTypeId,
  } = props.row.original;

  const systemClassifierItemsById = useSystemClassifierItemsBy(['OUTPUT_TYPE']);

  const items = useMemo(
    () => [
      {
        key: 'outputData',
        label: (
          <UiLink
            id="outputData"
            path={path.visualValidationCrossTable}
            params={{
              groupDescriptionRef: getGroupDescriptionRef(groupId, id),
              valueDate,
              version: activeDataObjectVersionId || undefined,
            }}
            target="_blank"
          >
            <DropdownItem>
              <TextNormal>{t('menu.outputData')}</TextNormal>
            </DropdownItem>
          </UiLink>
        ),
      },
      {
        key: 'view',
        label: (
          <UiLink
            id="view"
            path={path.outputDataObjectView}
            params={{
              id: activeDataObjectDescriptionVersionId!,
            }}
            target="_blank"
          >
            <DropdownItem>
              <TextNormal>{t('menu.view')}</TextNormal>
            </DropdownItem>
          </UiLink>
        ),
      },
      {
        key: 'relations',
        label: (
          <UiLink
            id="relations"
            path={path.relations}
            params={{
              type: systemClassifierItemsById['OUTPUT_TYPE'].get(outputTypeId)
                ?.code,
              groupOrDescriptionId: getGroupDescriptionRef(groupId, id),
              ...(activeDataObjectVersionId
                ? { version: activeDataObjectVersionId }
                : {}),
              valueDate,
              showOneStep: 'true',
              showIntermediates: 'false',
            }}
            target="_blank"
          >
            <DropdownItem>
              <TextNormal>{t('menu.relations')}</TextNormal>
            </DropdownItem>
          </UiLink>
        ),
      },
    ],
    [
      activeDataObjectDescriptionVersionId,
      systemClassifierItemsById,
      outputTypeId,
      id,
      activeDataObjectVersionId,
      valueDate,
      groupId,
    ],
  );

  if (!activeDataObjectDescriptionVersionId) return null;

  return (
    <Flex justify="end">
      <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
        <Button
          id={`output-calculation-action`}
          type="text"
          shape="circle"
          icon={<Icon.MoreVertical size={20} />}
          size="small"
        />
      </Dropdown>
    </Flex>
  );
});

const TableWrapper = styled.div<{ isRead?: boolean }>`
  .intermediate > td:nth-child(${(props) => (props.isRead ? 1 : 2)}) {
    padding-left: 40px;
  }
`;
